import {
  kApdmBlockReasonLabels,
  kApdmPreviewWarningLabels,
} from '@euler/functions/apdm/campaignLabels';
import {
  ApdmCampaignPreviewResult,
  RpaOutboundMessage,
} from '@euler/model/apdm';
import { Image, Text, View } from 'react-native';
import { useTheme } from 'styled-components';

function PreviewMessage({ message }: { message: RpaOutboundMessage }) {
  const theme = useTheme();
  if (message.kind === 'image')
    return (
      <Image
        accessibilityLabel="实际消息图片预览"
        source={{ uri: message.url }}
        resizeMode="contain"
        style={{ width: '100%', height: 220 }}
      />
    );
  if (message.kind === 'text')
    return (
      <View
        style={{
          backgroundColor: theme.page.background,
          padding: 12,
          borderRadius: 8,
          marginBottom: 8,
        }}
      >
        <Text style={{ color: theme.label.color }}>{message.text}</Text>
      </View>
    );
  if (message.kind === 'video')
    return (
      <Text style={{ color: theme.label.color }}>视频消息：{message.url}</Text>
    );
  return (
    <View
      style={{
        padding: 12,
        borderWidth: 1,
        borderColor: theme.form.input.borderColor,
        borderRadius: 8,
        marginBottom: 8,
      }}
    >
      <Text style={{ color: theme.label.color, fontWeight: '600' }}>
        {message.title}
      </Text>
      {message.kind === 'link' && message.desc && (
        <Text style={{ color: theme.label.color }}>{message.desc}</Text>
      )}
      {message.thumbUrl && (
        <Image
          accessibilityLabel="消息卡片缩略图"
          source={{ uri: message.thumbUrl }}
          resizeMode="contain"
          style={{ width: '100%', height: 140 }}
        />
      )}
      <Text style={{ color: theme.label.color, marginVertical: 8 }}>
        {message.kind === 'link' ? message.url : message.path}
      </Text>
    </View>
  );
}
export function WizardPreview({
  preview,
}: {
  preview: ApdmCampaignPreviewResult;
}) {
  const theme = useTheme();
  return (
    <>
      <Text style={{ color: theme.label.color }}>
        服务器统计时间：{preview.asOf} · 草稿版本 {preview.campaign.version}
      </Text>
      <Text style={{ color: theme.label.color, marginVertical: 12 }}>
        总人数 {preview.counts.total} · 已知可触达 {preview.counts.known} ·
        未确认可触达 {preview.counts.unconfirmed} · 已排除{' '}
        {preview.counts.excluded}
      </Text>
      {preview.counts.exclusionReasons.map(value => (
        <Text key={value.reason} style={{ color: theme.label.color }}>
          {kApdmBlockReasonLabels[value.reason]}：{value.count}
        </Text>
      ))}
      <Text style={{ color: theme.label.color, marginVertical: 12 }}>
        在线账号 {preview.capacity.liveAccounts} · 限流账号{' '}
        {preview.capacity.throttledAccounts} · 窗口容量上界{' '}
        {preview.scheduledCapacityUpperBound ?? '未知'}
      </Text>
      {preview.capacity.earliestAvailableAt && (
        <Text style={{ color: theme.label.color }}>
          最早解除限流：{preview.capacity.earliestAvailableAt}
        </Text>
      )}
      {preview.warnings.map(value => (
        <Text key={value} style={{ color: theme.label.color, marginBottom: 8 }}>
          {kApdmPreviewWarningLabels[value]}
        </Text>
      ))}
      {preview.campaign.couponCampaignId && (
        <Text style={{ color: theme.label.color, marginVertical: 12 }}>
          优惠券样例图片／二维码仅供预览，不可核销。
        </Text>
      )}
      <Text style={{ color: theme.label.color, marginTop: 16 }}>
        全部文案逐条审核（不是实际发送记录）
      </Text>
      {preview.campaign.variants.map((variant, variantIndex) => {
        const example = preview.variantExamples?.find(
          entry => entry.variantIndex === variantIndex,
        );
        return (
          <View key={variantIndex} style={{ marginTop: 12 }}>
            <Text style={{ color: theme.label.color }}>
              文案 {variantIndex + 1}
            </Text>
            {example?.kind === 'ready' ? (
              <>
                <Text style={{ color: theme.label.color }}>{example.text}</Text>
                <Text style={{ color: theme.label.color }}>
                  {example.applicabilityMatches
                    ? '真实候选资料示例；实际发送仍由服务器选择适用文案。'
                    : '假设性文案审核：这位候选人的保养依据不适用此文案，不会这样实际发送。'}
                </Text>
              </>
            ) : (
              <>
                <Text style={{ color: theme.label.color }}>{variant.text}</Text>
                <Text style={{ color: theme.label.color }}>
                  {example?.kind === 'unavailable'
                    ? `真实样例缺少字段：${example.missingFields.join(
                        '、',
                      )}，未虚构客户资料。`
                    : preview.samples.length === 0
                    ? '当前没有符合条件的真实样例，请审核原始文案；持续活动仍可确认。'
                    : '缺少逐条文案预览，请重新请求服务器预览后再确认。'}
                </Text>
              </>
            )}
          </View>
        );
      })}
      {preview.samples.slice(0, 3).map((sample, index) => (
        <View key={index} style={{ marginTop: 20 }}>
          <Text
            style={{
              color: theme.label.color,
              fontWeight: '600',
              marginBottom: 8,
            }}
          >
            {sample.candidate.licensePlateNo} · 样例 {index + 1}
          </Text>
          {sample.kind === 'ready' ? (
            sample.messages.map((message, ordinal) => (
              <PreviewMessage key={ordinal} message={message} />
            ))
          ) : (
            <Text style={{ color: theme.label.color }}>
              没有适用于此客户事实的文案，不能确认活动。请补全文案或所需事实后保存，并重新获取实际消息预览。
            </Text>
          )}
        </View>
      ))}
      {preview.samples.length === 0 && (
        <Text style={{ color: theme.label.color }}>
          当前没有可展示的实际样例。
        </Text>
      )}
    </>
  );
}
